import { useSyncExternalStore } from 'react';

import type { ToastProps } from '@/components/ui/Toast';

/**
 * Lightweight toast store. Imperative API for code outside React tree
 * (TanStack Query mutation `onError`, fetch interceptors, etc.):
 *
 *   import { toast } from '@/hooks/useToast';
 *   toast.success('Saved');
 *   toast.error('Save failed', { description: err.message });
 *
 * Inside components, use `useToast()` to read the queue (only `<Toaster />`
 * should need that — feature code calls the imperative API).
 */

export interface ToastEntry {
  id: string;
  variant: NonNullable<ToastProps['variant']>;
  title: string;
  description?: string;
  /** Auto-dismiss after N ms. 0 = manual close only. Default 4000. */
  duration?: number;
}

type Listener = () => void;

class ToastStore {
  private toasts: ToastEntry[] = [];
  private listeners = new Set<Listener>();

  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };

  getSnapshot = () => this.toasts;

  push = (entry: Omit<ToastEntry, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    this.toasts = [...this.toasts, { id, ...entry }];
    this.emit();
    return id;
  };

  dismiss = (id: string) => {
    this.toasts = this.toasts.filter((t) => t.id !== id);
    this.emit();
  };

  private emit() {
    for (const l of this.listeners) l();
  }
}

const store = new ToastStore();

export interface ToastOptions {
  description?: string;
  duration?: number;
}

export const toast = {
  info: (title: string, opts: ToastOptions = {}) =>
    store.push({ variant: 'info', title, ...opts }),
  success: (title: string, opts: ToastOptions = {}) =>
    store.push({ variant: 'success', title, ...opts }),
  warning: (title: string, opts: ToastOptions = {}) =>
    store.push({ variant: 'warning', title, ...opts }),
  error: (title: string, opts: ToastOptions = {}) =>
    store.push({ variant: 'error', title, ...opts }),
  dismiss: (id: string) => store.dismiss(id),
};

export function useToast() {
  const toasts = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return { toasts, dismiss: store.dismiss };
}
